import React, { PureComponent } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import { onlyNumbers } from "utils/normalize";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { TextField } from "components/Fields";
import { required, isLessThan20digits, accountNumMustMatch, isNumEqual9, isCardBinComplete } from 'utils/validations';
import { tokenSelector } from "containers/EmbeddableWidget/selectors";

import "./styles.scss";
import { CustomTooltip } from '../../../../../components/DataDisplay';
import AccountNumberTooltip from '../../../../../components/DataDisplay/AccountNumberTooltip';

const selector = formValueSelector('uown_bank_data_form');

const CARD_NAMES = { visa: 'Visa', mc: 'Mastercard', amex: 'American Express', discover: 'Discover' };

const CardIllustration = () => (
  <div className="uown-bank-data-form__card-illustration">
    <div className="uown-bank-data-form__card-illustration-label">Your card number</div>
    <div className="uown-bank-data-form__card-illustration-number">
      <div className="uown-bank-data-form__card-illustration-highlight">
        <span className="uown-bank-data-form__card-illustration-highlight-label">Enter these digits</span>
        4111 11
      </div>
      <span className="uown-bank-data-form__card-illustration-masked">** **** ****</span>
    </div>
    <div className="uown-bank-data-form__card-illustration-logo">
      <svg width="32" height="20" viewBox="0 0 32 20">
        <circle cx="11" cy="10" r="9" fill="#eb001b" opacity="0.6"/>
        <circle cx="21" cy="10" r="9" fill="#f79e1b" opacity="0.6"/>
      </svg>
    </div>
  </div>
);

class UownBankDataForm extends PureComponent {

  componentDidUpdate(prevProps) {
    if (prevProps.formData !== this.props.formData) {
      this.handleChange();
    }
  }

  detectCardType = (digits) => {
    if (!digits) return null;
    if (/^4/.test(digits))           return 'visa';
    if (/^5[1-5]/.test(digits))      return 'mc';
    if (/^2[2-7]/.test(digits))      return 'mc';
    if (/^3[47]/.test(digits))       return 'amex';
    if (/^6(?:011|5)/.test(digits))  return 'discover';
    return null;
  }

  handleChange = () => {
    const { formData, onDataChange, valid } = this.props || {};
    const cardBin = formData.cardBin || '';

    onDataChange({
      data: { ...formData, cardBin },
      valid: valid && cardBin.length === 6
    });
  }

  renderField = ({ input, meta, label, placeholder }) => (
    <TextField
      type="text"
      label={label}
      placeholder={placeholder}
      meta={meta}
      customChange={this.handleChange}
      {...input}
    />
  );

  renderConfirmField = ({ input, meta, label, placeholder }) => (
    <div className="uown-bank-data-form__confirm-field">
      <TextField
        type="text"
        label={label}
        placeholder={placeholder}
        meta={meta}
        customChange={this.handleChange}
        {...input}
      />
    </div>
  );

  renderCardBinField = ({ input, meta }) => {
    const clean = input.value || '';
    const display = clean.length > 4 ? `${clean.slice(0, 4)} ${clean.slice(4)}` : clean;
    const cardType = this.detectCardType(clean);
    const isComplete = clean.length === 6;

    return (
      <div className="uown-bank-data-form__card-bin-wrap">
        <div className={classNames('uown-bank-data-form__card-input-icon', {
          'uown-bank-data-form__card-input-icon--visa':     cardType === 'visa',
          'uown-bank-data-form__card-input-icon--mc':       cardType === 'mc',
          'uown-bank-data-form__card-input-icon--amex':     cardType === 'amex',
          'uown-bank-data-form__card-input-icon--discover': cardType === 'discover',
        })}>
          <svg width="16" height="11" viewBox="0 0 24 18" fill="none" stroke="white" strokeWidth="1.5">
            <rect x="1" y="1" width="22" height="16" rx="2"/>
            <line x1="1" y1="6" x2="23" y2="6"/>
          </svg>
        </div>
        <TextField
          type="text"
          label="FIRST 6 CARD DIGITS"
          placeholder="Enter first 6 digits"
          meta={meta}
          customChange={this.handleChange}
          maxLength={7}
          inputMode="numeric"
          autoComplete="off"
          {...input}
          value={display}
          onChange={(value) => input.onChange(value.replace(/\D/g, '').slice(0, 6))}
          onBlur={() => input.onBlur(clean)}
        />
        <div className={classNames('uown-bank-data-form__card-detected', {
          'uown-bank-data-form__card-detected--visible': cardType && isComplete
        })}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          <span>{cardType ? `${CARD_NAMES[cardType]} card detected` : ''}</span>
        </div>
      </div>
    );
  };

  render() {
    const { formData } = this.props;

    return (
      <div className="uown-bank-data-form__form">
        <div className="uown-bank-data-form__section">
          <div className="uown-bank-data-form__section-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
              <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/>
            </svg>
            <span className="uown-bank-data-form__section-title">Bank Account</span>
          </div>

          <div className="uown-bank-data-form__field">
            <Field
              name="routingNum"
              normalize={onlyNumbers}
              validate={[required, isNumEqual9]}
              label="ROUTING NUMBER"
              placeholder="9-digit routing number"
              component={this.renderField}
            />
            <span className="uown-bank-data-form__hint">9 digits</span>
          </div>

          <div className="uown-bank-data-form__field">
            <Field
              name="confirmRoutingNum"
              normalize={onlyNumbers}
              validate={[required, value => {
                return value !== formData.routingNum ? 'Routing numbers must match' : undefined;
              }]}
              label="CONFIRM ROUTING NUMBER"
              placeholder="Re-enter routing number"
              component={this.renderConfirmField}
            />
          </div>

          <div className="uown-bank-data-form__field">
            <Field
              name="accountNum"
              normalize={onlyNumbers}
              validate={[required, isLessThan20digits]}
              label="ACCOUNT NUMBER"
              placeholder="Account number"
              component={this.renderField}
            />
          </div>

          <div className="uown-bank-data-form__field">
            <Field
              name="confirmAccountNum"
              normalize={onlyNumbers}
              validate={[required, isLessThan20digits, accountNumMustMatch]}
              label="CONFIRM ACCOUNT NUMBER"
              placeholder="Re-enter account number"
              component={this.renderConfirmField}
            />
          </div>

          <div className="uown-bank-data-form__help-link">
            <CustomTooltip
              title="Where can I find my account numbers?"
              text={<AccountNumberTooltip />}
            />
          </div>
        </div>

        <div className="uown-bank-data-form__section">
          <div className="uown-bank-data-form__section-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            <span className="uown-bank-data-form__section-title">Card Verification</span>
          </div>
          <p className="uown-bank-data-form__section-desc">
            First 6 digits of the card that will be used for your initial payment — this helps verify your identity
          </p>

          <div className="uown-bank-data-form__bin-row">
            <CardIllustration />

            <div className="uown-bank-data-form__field">
              <Field
                name="cardBin"
                validate={[required, isCardBinComplete]}
                label="FIRST 6 CARD DIGITS"
                component={this.renderCardBinField}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

UownBankDataForm.propTypes = {
  onDataChange: PropTypes.func
};

UownBankDataForm.defaultProps = {
  onDataChange: () => {}
};

UownBankDataForm = reduxForm({
  form: "uown_bank_data_form",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true
})(UownBankDataForm);

UownBankDataForm = connect(
  state => ({
    formData: selector(state, 'routingNum', 'accountNum', 'confirmRoutingNum', 'confirmAccountNum', 'cardBin'),
    token: tokenSelector()(state)
  })
)(UownBankDataForm);

export default UownBankDataForm;
